import { lazy, Suspense, useEffect } from 'react';
import {
  BrowserRouter,
  Route,
  Routes,
  useNavigate,
} from 'react-router-dom';
import { EmptyState, Spinner } from '@/components/ui';
import { AppLayout } from '@/components/AppLayout';
import { ConfirmProvider } from '@/components/ConfirmProvider';
import { setUnauthenticatedHandler } from '@/lib/api/client';
import { LoginPage } from '@/features/auth/LoginPage';
import { HomePage } from '@/features/auth/HomePage';
import { ProtectedRoute } from '@/features/auth/ProtectedRoute';
import { MyInfoPanel } from '@/features/auth/MyInfoPanel';
import { AdminPage } from '@/features/admin/AdminPage';
import { RecommendationListPage } from '@/features/trading/RecommendationListPage';
import { JournalPage } from '@/features/trading/JournalPage';
import { AlertsPage } from '@/features/trading/AlertsPage';
import { LeadingPage } from '@/features/trading/LeadingPage';
import { SectorsPage } from '@/features/trading/SectorsPage';
import { MyTradesPage } from '@/features/trading/MyTradesPage';
import { AutoTradePage } from '@/features/trading/AutoTradePage';
import { RecommendationDetailPage } from '@/features/trading/RecommendationDetailPage';
import { AccountPage } from '@/features/trading/AccountPage';
import { PaperPage } from '@/features/trading/PaperPage';
import { ExchangeRatePage } from '@/features/trading/ExchangeRatePage';

/**
 * 가계부 화면을 따로 내려받는다. (3.14)
 *
 * 🔴 왜 가계부만인가
 *   가계부가 Recharts 를 쓴다. 그것이 첫 화면 번들에 들어가 있었다.
 *   단타만 보려는 사람도, 가계부 권한이 없는 사람도 매번 그것을 받았다.
 *
 *   단타의 lightweight-charts 는 이미 갈라져 있다 —
 *   CandleChart 를 모달 안에서 lazy 로 부른다. (36번 노트)
 *   같은 처리를 가계부에도 한 것이다.
 *
 * 라우트 단위로 가르는 이유
 *   가계부 화면 8개가 전부 같은 차트 라이브러리를 쓴다.
 *   화면마다 lazy 를 붙이면 조각이 8개로 늘고, 탭을 옮길 때마다
 *   로딩이 한 번씩 뜬다. 입구에서 한 번 받고 그 뒤로는 조용한 편이 낫다.
 *
 * 🔴 권한이 없는 사람은 이 조각을 아예 안 받는다
 *   ProtectedRoute 가 먼저 막아 lazy 컴포넌트에 닿지 않는다.
 *   가계부는 본인만 쓰므로 다른 가족은 이 용량을 낼 이유가 없다.
 */
const LedgerRoutes = lazy(() =>
  import('@/features/ledger/LedgerRoutes').then((module) => ({
    default: module.LedgerRoutes,
  })),
);

// 혜택도 같은 방식이다. 번들을 가르는 이유와 경계는 위 LedgerRoutes 주석 참조.
const BenefitRoutes = lazy(() =>
  import('@/features/benefit/BenefitRoutes').then((module) => ({
    default: module.BenefitRoutes,
  })),
);

/**
 * 라우터.
 *
 * 🔴 이 파일은 공유 영역이다. 단독 세션에서만 고친다. (CLAUDE.md 4.1절)
 *   도메인 세션이 화면을 추가할 때는 자기 features/ 아래에 컴포넌트를 만들고
 *   사용자에게 "라우트 등록이 필요하다" 고 알린다.
 *
 * 권한은 두 겹이다.
 *   ProtectedRoute  화면을 그릴지 말지 (사용자 경험)
 *   백엔드 필터체인  API 를 열지 말지 (실제 차단)
 * 앞의 것만 있으면 URL 을 직접 쳐서 뚫린다. 뒤의 것만 있으면
 * 권한 없는 화면을 다 그린 뒤에 403 이 뜬다. 둘 다 필요하다.
 *
 * menuCode 는 cm_menu.code 와 같아야 한다. 틀리면 해당 화면이 아무에게도 안 열린다.
 */
export default function App() {
  return (
    /*
      ConfirmProvider 가 BrowserRouter 안쪽에 있는 이유
        확인창은 라우터와 무관하지만, 바깥에 두면 확인창 안에서 Link 를 쓸 수 없다.
        "이 항목을 지울까요? (자세히 보기)" 같은 문구를 넣을 여지를 남겨둔다.
    */
    <BrowserRouter>
      <ConfirmProvider>
        <SessionExpiryRedirect />

        <Routes>
        <Route path="/login" element={<LoginPage />} />

        <Route element={<AppLayout />}>
          <Route
            index
            element={
              <ProtectedRoute>
                <HomePage />
              </ProtectedRoute>
            }
          />

          {/* 단기매매 — 종목·추천. 공용 메뉴다 */}
          <Route
            path="trading"
            element={
              <ProtectedRoute menuCode="TRADING">
                <RecommendationListPage />
              </ProtectedRoute>
            }
          />

          {/*
            지난 추천 — 이력과 누적 통계. (11단계 ⑤⑥)

            🔴 trading/:id 보다 먼저 선언한다
              React Router 7 은 정적 세그먼트를 동적 세그먼트보다 우선하므로
              순서와 무관하게 동작하지만, journal 이 :id 로 읽히면
              숫자가 아닌 id 로 상세를 부르게 되어 원인이 안 보인다.
              읽는 사람이 헷갈리지 않게 위에 둔다.

            TRADING 권한으로 막는다. 추천 목록과 같은 데이터다.
          */}
          <Route
            path="trading/journal"
            element={
              <ProtectedRoute menuCode="TRADING">
                <JournalPage />
              </ProtectedRoute>
            }
          />

          {/* 주도주. :id 보다 먼저 둔다 (V128) */}
          <Route
            path="trading/leading"
            element={
              <ProtectedRoute menuCode="TRADING">
                <LeadingPage />
              </ProtectedRoute>
            }
          />

          {/* 섹터별 종목. :id 보다 먼저 둔다 (2026-08-23) */}
          <Route
            path="trading/sectors"
            element={
              <ProtectedRoute menuCode="TRADING">
                <SectorsPage />
              </ProtectedRoute>
            }
          />

          {/* 가격 알림 현황. journal 과 같은 이유로 :id 보다 먼저 둔다 (2026-08-23) */}
          <Route
            path="trading/alerts"
            element={
              <ProtectedRoute menuCode="TRADING">
                <AlertsPage />
              </ProtectedRoute>
            }
          />

          {/*
            AI 모의투자 — 두 프로필 계좌와 비교. (18단계)

            🔴 trading/:id 보다 먼저 선언한다. journal 과 같은 이유다
              React Router 7 은 정적 세그먼트를 우선하므로 순서와 무관하게 동작하지만,
              paper 가 :id 로 읽히면 숫자 아닌 id 로 상세를 부르게 되어 원인이 안 보인다.

            TRADING 권한으로 막는다. 사용자의 실제 잔고가 없고 가상 계좌 두 개뿐이라
            계좌 화면(TRADING_ACCOUNT)처럼 따로 가르지 않는다.
          */}
          <Route
            path="trading/paper"
            element={
              <ProtectedRoute menuCode="TRADING">
                <PaperPage />
              </ProtectedRoute>
            }
          />

          {/*
            환율 - 달러 → 원. :id 보다 먼저 둔다 (2026-09-19, I-61)
            사용자가 대메뉴 대신 단타 안의 탭으로 정했다 - 권한 칸을 새로 만들지 않고 TRADING 으로 연다.
          */}
          <Route
            path="trading/fx"
            element={
              <ProtectedRoute menuCode="TRADING">
                <ExchangeRatePage />
              </ProtectedRoute>
            }
          />

          {/*
            추천 상세.
            trading/account 보다 먼저 선언하지 않는다 — :id 가 account 를 삼킨다.
            React Router 7 은 정적 세그먼트를 동적 세그먼트보다 우선하므로
            순서와 무관하게 동작하지만, 읽는 사람이 헷갈리지 않게 뒤에 둔다.
          */}
          <Route
            path="trading/:id"
            element={
              <ProtectedRoute menuCode="TRADING">
                <RecommendationDetailPage />
              </ProtectedRoute>
            }
          />

          {/*
            계좌·보유·체결. 본인만 본다.
            토스 API 키가 하나뿐이라 다른 사람에게 열어줘도 본인 계좌가 보일 뿐이다.
            (00-architecture.md 1절)
          */}
          <Route
            path="trading/account"
            element={
              <ProtectedRoute menuCode="TRADING_ACCOUNT">
                <AccountPage />
              </ProtectedRoute>
            }
          />

          {/*
            매매일지 — 내 실제 체결. (13단계)

            🔴 TRADING 이 아니라 TRADING_ACCOUNT 다 (2026-08-28 이동)
              처음에는 trading/my-trades 였다. TRADING 은 스크리너가 만든 공용
              결과를 보는 메뉴라 가족 모두에게 열린다 — 거기 두면 동생이
              로그인해도 형의 실제 체결·평단·손익이 그대로 보인다.
              백엔드 경로도 /api/trading/account/my-trades 로 함께 옮겼다.
              (MyTradeController · MenuCatalogTest)
          */}
          <Route
            path="trading/account/my-trades"
            element={
              <ProtectedRoute menuCode="TRADING_ACCOUNT">
                <MyTradesPage />
              </ProtectedRoute>
            }
          />

          {/*
            자동매매 — 모드 · 한도 · 주문 시도 기록. (2026-09-20, BACKLOG I-5 A3)

            🔴 여기도 TRADING_ACCOUNT 다. 내 실계좌를 움직이는 설정이라
              my-trades 와 같은 이유로 가족에게 열지 않는다.
            🔴 지금은 보기만 하는 화면이다 - 모드를 바꾸려면 재인증(A5)이 먼저다.
          */}
          <Route
            path="trading/account/autotrade"
            element={
              <ProtectedRoute menuCode="TRADING_ACCOUNT">
                <AutoTradePage />
              </ProtectedRoute>
            }
          />

          {/*
            가계부 — 세션 B 담당.
            하위 경로(transactions / analysis / ...)는 LedgerRoutes 가 소유한다.
            화면을 더 붙일 때 이 공유 파일을 다시 열지 않아도 되게 한 것이다.
          */}
          <Route
            path="ledger/*"
            element={
              <ProtectedRoute menuCode="LEDGER">
                {/*
                  Suspense 를 ProtectedRoute 안쪽에 둔다.
                  바깥에 두면 권한이 없는 사람에게도 스피너가 한 번 뜬 뒤에
                  "접근 권한이 없습니다" 로 바뀐다.
                */}
                <Suspense fallback={<Spinner label="가계부를 불러오는 중" />}>
                  <LedgerRoutes />
                </Suspense>
              </ProtectedRoute>
            }
          />

          {/*
            혜택 — Phase 4 시작 (2026-08-20, 구현 순서 0번).
            하위 경로(holdings / ...)는 BenefitRoutes 가 소유한다.
          */}
          <Route
            path="benefit/*"
            element={
              <ProtectedRoute menuCode="BENEFIT">
                <Suspense fallback={<Spinner label="혜택을 불러오는 중" />}>
                  <BenefitRoutes />
                </Suspense>
              </ProtectedRoute>
            }
          />

          {/*
            관리자. menuCode 가 아니라 adminOnly 로 막는다.
            ADMIN 메뉴 권한을 실수로 꺼도 관리자 자신은 들어올 수 있어야
            권한을 되돌릴 수 있다.
          */}
          <Route
            path="admin/*"
            element={
              <ProtectedRoute adminOnly>
                <AdminPage />
              </ProtectedRoute>
            }
          />

          {/*
            내 정보. (3.6)

            🔴 menuCode 를 주지 않는다. cm_menu 에 없는 화면이다
              누구나 자기 정보는 고칠 수 있어야 하므로 권한으로 끊을 것이 없다.
              메뉴로 만들면 관리자가 실수로 끄는 순간 그 사람은 자기 비밀번호도
              못 바꾸게 된다. 로그인 여부만 확인한다.

            관리자에게는 상단 탭으로 보이지 않지만 이 경로는 열려 있다.
            (관리자는 /admin 안의 '내 정보' 탭으로 간다 — AdminPage 주석)
            주소를 직접 쳐서 들어와도 같은 화면이 뜨는 것이 맞다.
          */}
          <Route
            path="me"
            element={
              <ProtectedRoute>
                <MyInfoPanel />
              </ProtectedRoute>
            }
          />

          <Route
            path="*"
            element={<EmptyState title="없는 화면입니다." description="주소를 확인해 주세요." />}
          />
          </Route>
        </Routes>
      </ConfirmProvider>
    </BrowserRouter>
  );
}

/**
 * 세션이 끊기면 로그인 화면으로 보낸다.
 *
 * api/client.ts 의 인터셉터는 React 바깥이라 라우터를 쓸 수 없다.
 * 그래서 이동 함수를 여기서 꽂아준다.
 *
 * window.location 으로 밀어버리지 않는 이유
 *   SPA 가 통째로 다시 로딩되어 캐시와 입력 중이던 값이 전부 날아간다.
 *   세션 만료는 30일에 한 번 일어나는 일이 아니라, 자리를 비웠다 돌아올 때마다 일어난다.
 */
function SessionExpiryRedirect() {
  const navigate = useNavigate();

  useEffect(() => {
    setUnauthenticatedHandler(() => {
      navigate('/login', { replace: true });
    });

    return () => setUnauthenticatedHandler(null);
  }, [navigate]);

  return null;
}

// ComingSoon 자리지킴이는 혜택이 실제 화면을 갖게 되면서 없어졌다 (2026-08-20).
// 마지막 사용처가 혜택이었다 - 라우트를 먼저 잡아 권한 구조를 검증하는 역할은 끝났다.
